// src/prints/PrintableInvoice.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import Logo from "../assets/infraLogo.jpg";
import { useParams } from "react-router-dom";

const API_BASE =
  import.meta.env?.VITE_API_BASE ||
  process.env.REACT_APP_API_BASE ||
  "https://rfs2.infraconsoft.net.in/api";

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const PrintableInvoice = () => {
  useAuthRedirect();
  const { prNo } = useParams();
  const branch = localStorage.getItem("branch");

  const [indentView, setIndentView] = useState({});
  const [data, setData] = useState([]);

  const [supplierCode, setSupplierCode] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [prepareBy, setPrepareBy] = useState("");

  const [loadingItems, setLoadingItems] = useState(false);

  // ---- Site / Company info for header ----
  const [site, setSite] = useState({
    ok: true,
    name: "Infraconsoft",
    logo: null,
    address: "",
    email: "",
    phone: "",
    _ts: 0,
  });

  // Fetch site/company info
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/siteinfo`);
        const s = res?.data || {};
        // ensure absolute logo URL if server ever returns relative
        let logo = s.logo || null;
        if (logo && !/^https?:\/\//i.test(logo)) {
          if (!logo.startsWith("/")) logo = `/${logo}`;
          logo = `${API_ORIGIN}${logo}`;
        }
        if (!ignore) {
          setSite({
            ok: !!s.ok,
            name: s.name || "Infraconsoft",
            logo,
            address: s.address || "",
            email: s.email || "",
            phone: s.phone || "",
            _ts: Date.now(),
          });
        }
      } catch (e) {
        // keep defaults on failure
        console.error("Failed to load /siteinfo", e);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  // Fetch header
  useEffect(() => {
    const fetchIndentDetails = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/prApproval_detailFinal?prNo=${prNo}`
        );
        if (response.data && response.data.headerDetails?.length > 0) {
          const items = response.data.headerDetails;

          const storeReqHeader = {
            date: items[0].prHeader_date || "N/A",
            branchCode: items[0].branchCode || "N/A",
            indenter: items[0].prHeader_hod || "N/A",
            indentNo: items[0].prHeader_indentNumber || "N/A",
            fld_requbasis: items[0].prHeader_remarks || "N/A",
            hm: items[0].prHeader_headMaterial || "N/A",
            fld_prepared: items[0].fld_prepared || "N/A",
            remarks: items[0].prHeader_remarks || "N/A",
          };

          setIndentView(storeReqHeader);
        }
      } catch (error) {
        console.error("Error fetching header details:", error);
      }
    };

    fetchIndentDetails();
  }, [prNo]);

  // Fetch items (bottom details)
  useEffect(() => {
    const fetchBottom = async () => {
      setLoadingItems(true);
      try {
        const res = await axios.get(
          `${API_BASE}/prApproval_detailFinalBottom?prNo=${prNo}&branch1=${indentView.branchCode}&branch=${branch}`
        );
        const arr = res?.data?.bottomDetails || [];
        setData(arr);

        if (arr.length > 0) {
          setSupplierCode(arr[0].supplierMaster_code || "");
          setSupplierName(arr[0].supplierMaster_name || "");
          setPrepareBy(arr[0].prDetail_prepared || "");
        }
      } catch (error) {
        console.error("Error fetching bottom details:", error);
      } finally {
        setLoadingItems(false);
      }
    };

    if (prNo && indentView.branchCode && branch) {
      fetchBottom();
    }
  }, [prNo, indentView.branchCode, branch]);

  const logoSrc = site.logo ? `${site.logo}${site._ts ? `?v=${site._ts}` : ""}` : Logo;

  return (
    <div className="prPrint-container">
      <div className="prPrint-header">
        <img src={logoSrc} alt={`${site.name} Logo`} className="prPrint-logo" />
        <div className="prPrint-company-details">
          <h2 style={{ textTransform: "uppercase" }}>{site.name || "Infraconsoft"}</h2>
          {site.address && <p>{site.address}</p>}
          {(site.phone || site.email) && (
            <p>
              {site.phone && <span>Ph: {site.phone}</span>}
              {site.phone && site.email && <span> | </span>}
              {site.email && <span>Email: {site.email}</span>}
            </p>
          )}
        </div>
      </div>

      <table className="prPrint-info-table">
        <tbody>
          <tr>
            <td>
              <strong>Number:</strong> {prNo}
            </td>
            <td>
              <strong>Date:</strong>{" "}
              {indentView.date
                ? new Date(indentView.date).toISOString().split("T")[0]
                : ""}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Branch:</strong> {branch}
            </td>
            <td>
              <strong>Supplier:</strong> {supplierCode} - {supplierName}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Indent Ref.:</strong> {indentView.indentNo}
            </td>
            <td>
              <strong>Indenter:</strong> {indentView.indenter}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="prPrint-item-table">
        <thead>
          <tr>
            <th>S.No</th>
            <th>Item Code</th>
            <th>Item Name</th>
            <th>UOM</th>
            <th>Price</th>
            <th>Qty</th>
            <th>Due Date</th>
          </tr>
        </thead>
        <tbody>
          {loadingItems ? (
            <tr>
              <td colSpan="7" style={{ textAlign: "center", fontStyle: "italic" }}>
                Loading items…
              </td>
            </tr>
          ) : data.length > 0 ? (
            (() => {
              let totalNetAmount = 0;
              let totalNetTax = 0;
              let totalFinalAmount = 0;

              return (
                <>
                  {data.map((item, index) => {
                    const price = Number(item.prDetail_price || 0);
                    const qty = Number(item.prDetail_quantity || 0);
                    const netamount = price * qty;

                    const discPct = Number(item.supplierWIdetail_discountPercentage || 0);
                    const netDISC = discPct === 0 ? 0 : (discPct / 100) * netamount;
                    const minDisc = netamount - netDISC;

                    const taxPct = Number(item.SupplierWIDetail_CSTtax || 0);
                    const netTax = taxPct === 0 ? 0 : (taxPct / 100) * minDisc;

                    const totalAmt = minDisc + netTax;

                    totalNetAmount += netamount;
                    totalNetTax += netTax;
                    totalFinalAmount += totalAmt;

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.prDetail_itemcode}</td>
                        <td>{item.itemmaster_name}</td>
                        <td>{item.itemMaster_purchaseUom}</td>
                        <td>{price}</td>
                        <td>{qty}</td>
                        <td>
                          {item.prDetail_duedate
                            ? new Date(item.prDetail_duedate).toLocaleDateString("en-GB")
                            : ""}
                        </td>
                      </tr>
                    );
                  })}

                  <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                    <td colSpan="6" style={{ textAlign: "right" }}>
                      Total Price
                    </td>
                    <td>{totalNetAmount.toFixed(2)}</td>
                  </tr>
                  <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                    <td colSpan="6" style={{ textAlign: "right" }}>
                      Total GST
                    </td>
                    <td>{totalNetTax.toFixed(2)}</td>
                  </tr>
                  <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                    <td colSpan="6" style={{ textAlign: "right" }}>
                      Total Amount
                    </td>
                    <td>{totalFinalAmount.toFixed(2)}</td>
                  </tr>
                </>
              );
            })()
          ) : (
            <tr>
              <td colSpan="7" style={{ textAlign: "center" }}>
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="prPrint-footer">
        <p>
          <strong>Prepared by : </strong>
          {prepareBy}
        </p>
      </div>

      <div className="prPrint-footer">
        <p>
          <strong>Approve by :</strong>
        </p>
      </div>

      <button className="prPrint-button" onClick={() => window.print()}>
        Print
      </button>
    </div>
  );
};

export default PrintableInvoice;
